(() => {
  const choices = new WeakMap();

  class CartStyleWith extends HTMLElement {
    connectedCallback() {
      const abort = this.abort = new AbortController();
      this.frame = null;
      customElements.whenDefined('cart-drawer').then(() => {
        if (!this.isConnected || abort.signal.aborted) return;
        this.drawer = this.closest('cart-drawer');
        this.scroller = this.drawer.shadowRoot.querySelector('[part="content"]');
        this.footer = this.drawer.shadowRoot.querySelector('[part="footer"]');
        this.header = this.drawer.shadowRoot.querySelector('[part="header"]');
        this.lines = this.drawer.querySelector('[data-cart-line-items]');
        this.body = this.closest('.cart-drawer__body-content');
        this.button = this.querySelector('.cart-drawer__recommendation-toggle');
        this.panel = this.querySelector('.cart-drawer__recommendation-panel');
        this.items = this.querySelector('.cart-drawer__recommendation-items');
        this.choice = choices.get(this.drawer) ?? null;
        this.choiceScroll = this.scroller.scrollTop;
        this.expanded = false;
        this.removeAttribute('data-ready');
        this.toggleAttribute('data-expanded', true);
        this.dataset.enhanced = '';
        const options = { signal: this.abort.signal };
        this.button.addEventListener('click', () => {
          this.choice = !this.expanded;
          this.choiceScroll = this.scroller.scrollTop;
          choices.set(this.drawer, this.choice);
          this.apply(this.choice ? this.panelHeight : 0, true);
        }, options);
        this.scroller.addEventListener('scroll', () => {
          if (this.choice !== null && Math.abs(this.scroller.scrollTop - this.choiceScroll) > 80) {
            this.choice = null;
            choices.delete(this.drawer);
          }
          this.schedule();
        }, { ...options, passive: true });
        this.drawer.addEventListener('dialog:before-show', () => {
          this.choice = null;
          choices.delete(this.drawer);
          this.schedule();
        }, options);
        this.drawer.addEventListener('dialog:after-show', () => this.schedule(), options);
        this.addEventListener('focusin', () => this.schedule(), options);
        this.addEventListener('focusout', () => this.schedule(), options);
        this.observer = new ResizeObserver(() => this.schedule());
        for (const element of [this.scroller, this.header, this.footer, this.lines, this.items]) {
          this.observer.observe(element);
        }
        this.schedule();
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      cancelAnimationFrame(this.readyFrame);
      this.frame = null;
    }

    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.measure();
      });
    }

    measure() {
      const height = this.scroller.clientHeight;
      if (!height || !this.drawer.hasAttribute('open')) return;
      const footerHeight = this.footer.getBoundingClientRect().height;
      const available = height - this.header.getBoundingClientRect().height - footerHeight;
      const headingHeight = this.button.getBoundingClientRect().height;
      const peek = Math.min(72, Math.max(0, available - headingHeight - 96));
      const cap = Math.max(0, available - headingHeight - peek);
      this.style.setProperty('--style-footer-height', `${footerHeight}px`);
      this.style.setProperty('--style-panel-max-height', `${cap}px`);
      const panelHeight = this.panelHeight = Math.min(this.items.scrollHeight, cap);
      const naturalHeight = this.lines.getBoundingClientRect().height + headingHeight + panelHeight;
      this.body.style.minHeight = `${naturalHeight}px`;
      const room = this.scroller.getBoundingClientRect().bottom - footerHeight - headingHeight;
      const availableReveal = room - this.lines.getBoundingClientRect().bottom;
      const fits = naturalHeight <= available + 1;
      const automatic = fits ? panelHeight : Math.max(0, Math.min(panelHeight, availableReveal));
      const focusedInside = this.panel.contains(document.activeElement);
      const reveal = focusedInside ? panelHeight : this.choice === null ? automatic : this.choice ? panelHeight : 0;
      this.apply(reveal, this.choice !== null);
      if (!this.hasAttribute('data-ready')) {
        this.readyFrame = requestAnimationFrame(() => { this.dataset.ready = ''; });
      }
    }

    apply(height, animate = false) {
      const expanded = height > 0;
      this.expanded = expanded;
      this.style.setProperty('--style-reveal-height', `${height}px`);
      this.style.setProperty('--style-plus-angle', `${this.panelHeight ? height / this.panelHeight * 45 : 0}deg`);
      this.toggleAttribute('data-manual', animate);
      this.toggleAttribute('data-expanded', expanded);
      this.toggleAttribute('data-fully-expanded', expanded && height >= this.panelHeight - 0.5);
      this.button.setAttribute('aria-expanded', String(expanded));
      this.panel.inert = !expanded;
      this.panel.setAttribute('aria-hidden', String(!expanded));
    }
  }

  if (!customElements.get('cart-style-with')) customElements.define('cart-style-with', CartStyleWith);
})();
